<template>
  <div class="relative inline-block">
    <Float :placement="position" portal show>
      <span
        :class="{ 'cursor-pointer': !disabled && clickable }"
        @mouseenter="isTooltipVisible = true"
        @mouseleave="isTooltipVisible = false"
      >
        <slot />
      </span>
      <div>
        <div v-if="isTooltipVisible && !disabled" :class="tooltipClass">
          <div
            class="whitespace-nowrap rounded bg-gray-800 p-2 text-sm leading-6 text-white dark:bg-gray-100 dark:text-gray-600"
          >
            {{ text }}
            <!--Floating UI supports arrows: https://headlessui-float.vercel.app/vue/arrow. However, it's not optimized for tooltips and requires heavy customization, so we use a custom arrow. If more positions are added, switching to Floating UI's arrow might be worth considering. -->
            <div v-if="hasArrow" :class="arrowClass" />
          </div>
        </div>
      </div>
    </Float>
  </div>
</template>

<script setup lang="ts">
type ToolTipPosition = 'top' | 'bottom' | 'left' | 'right';

const props = defineProps({
  text: String,
  position: {
    type: String as PropType<ToolTipPosition>,
    default: 'top',
  },
  hasArrow: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  clickable: {
    type: Boolean,
    default: true,
  },
});

const isTooltipVisible = ref(false);

const tooltipClass = computed(() => {
  return `absolute z-10 text-center duration-[0.3ms] tooltip-${props.position}`;
});

const arrowClass = computed(() => {
  return `absolute w-0 h-0 arrow-${props.position}`;
});
</script>

<style scoped>
.arrow-top {
  @apply bottom-[-8px] left-1/2 translate-x-[-50%] border-l-8 border-r-8 border-t-8 border-transparent border-solid border-t-gray-800;
}

.arrow-bottom {
  @apply bottom-full left-1/2 translate-x-[-50%] border-l-8 border-r-8 border-b-8 border-transparent border-solid border-b-gray-800;
}

.arrow-left {
  @apply left-full top-1/2 translate-y-[-50%] border-l-8 border-t-8 border-b-8 border-transparent border-solid border-l-gray-800;
}

.arrow-right {
  @apply right-full top-1/2 translate-y-[-50%] border-r-8 border-b-8 border-t-8 border-transparent border-solid border-r-gray-800;
}

.tooltip-bottom {
  @apply left-1/2 translate-x-[-50%] top-[calc(100%+10px)];
}

.tooltip-left {
  @apply top-1/2 translate-y-[-50%] right-[calc(100%+10px)];
}

.tooltip-right {
  @apply top-1/2 translate-y-[-50%] left-[calc(100%+10px)];
}

.tooltip-top {
  @apply left-1/2 translate-x-[-50%] bottom-[calc(100%+10px)];
}
</style>
